import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { api } from "../lib/api";
import { useUnits, formatRadius } from "../lib/units";
import TrailCard from "../components/TrailCard";
import BestWalkCard from "../components/BestWalkCard";
import SearchSheet from "../components/SearchSheet";
import BasecampsCarousel from "../components/BasecampsCarousel";
import { LOGO_URL } from "../lib/brand";
import { track, rememberRegion } from "../lib/telemetry";import { useOfflineIds } from "../lib/useOfflineIds";
import { trailMatchesAllAmenities } from "../lib/amenities";
import { Loader2, Mountain, Tent, Sparkles, MapPin, Cloud, PawPrint, SlidersHorizontal, X, Search, Trees, Droplets, Utensils } from "lucide-react";

// London fallback so the UI is never empty if geolocation is denied
const FALLBACK_COORDS = { lat: 51.5074, lng: -0.1278, label: "London" };

const TYPES = [
  { key: "", label: "All", Icon: Sparkles },
  { key: "hiking", label: "Hiking", Icon: Mountain },
  { key: "camping", label: "Camping", Icon: Tent },
];

const RADII = [5, 20, 50];

// Universal amenity toggles — keys match the backend `amenities` param.
const AMENITIES = [
  { key: "shaded", label: "Shaded", desc: "Tree cover or shaded paths for hot days.", Icon: Trees },
  { key: "water_tap", label: "Water Tap", desc: "Drinking water for the pack en route.", Icon: Droplets },
  { key: "picnic_area", label: "Picnic Area", desc: "Somewhere to unpack lunch.", Icon: Utensils },
];

export default function Discover() {
  const navigate = useNavigate();
  const { units } = useUnits();
  const [coords, setCoords] = useState(null);
  const [coordsLabel, setCoordsLabel] = useState(null);
  const [geoState, setGeoState] = useState("prompting"); // prompting | granted | denied | place
  const [type, setType] = useState("");
  const [radius, setRadius] = useState(20);
  const [goodWeatherOnly, setGoodWeatherOnly] = useState(false);
  const [dogFriendlyOnly, setDogFriendlyOnly] = useState(false);
  const [amenityFilters, setAmenityFilters] = useState([]);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const { ids: offlineIds } = useOfflineIds();
  const activeFilterCount = (goodWeatherOnly ? 1 : 0) + (dogFriendlyOnly ? 1 : 0) + (radius !== 20 ? 1 : 0) + amenityFilters.length;
  // Amenity-only badge count for the Filters button — walkers scan this to see
  // how many "Shaded / Water Tap / Picnic Area" toggles are active without
  // opening the sheet. Total activeFilterCount still drives the button's
  // active styling below.
  const amenityBadgeCount = amenityFilters.length;

  const toggleAmenity = (key) => {
    setAmenityFilters((prev) => {
      const next = prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key];
      // Only log applications (not removals) — mirrors the lunch-chip pattern.
      if (!prev.includes(key)) track("filter_change", { filter: "amenity", amenity: key, surface: "campsites" });
      return next;
    });
  };

  useEffect(() => {
    if (!navigator.geolocation) {
      setCoords(FALLBACK_COORDS);
      setGeoState("denied");
      // No region signal without geolocation — track anyway so page-load
      // counts stay honest, region will be "Unknown".
      track("discover_view", { geo: "unsupported" });
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const c = { lat: pos.coords.latitude, lng: pos.coords.longitude };
        setCoords(c);
        // Cache for other pages (TransitSection auto-fills "Use my location"
        // instantly instead of waiting for a fresh permission-round-trip).
        try {
          localStorage.setItem("td_last_coords", JSON.stringify({ ...c, ts: Date.now() }));
        } catch { /* ignore */ }
        setGeoState("granted");
        // Cold-start reverse-geocode: fire the lookup, cache the region,
        // THEN fire discover_view so this session's very first event lands
        // on the admin heatmap with the right region instead of "Unknown".
        api.get("/map/reverse", { params: { lat: c.lat, lng: c.lng } })
          .then((r) => {
            if (r.data?.region) rememberRegion(r.data.region);
          })
          .catch(() => { /* silent — heatmap gracefully degrades */ })
          .finally(() => {
            track("discover_view", { geo: "granted" });
          });
      },
      () => {
        setCoords(FALLBACK_COORDS);
        setGeoState("denied");
        track("discover_view", { geo: "denied" });
      },
      { timeout: 8000, maximumAge: 600000 }
    );
  }, []);

  const enabled = !!coords;

  const list = useQuery({
    // Amenity filters no longer participate in the query key — we filter
    // the already-loaded list client-side against the trail's own
    // `dog_amenities` tags (see `filtered` below), so toggling a chip
    // is instant with zero re-fetch.
    queryKey: ["locations", coords, radius, type, dogFriendlyOnly],
    enabled,
    queryFn: async () => {
      const r = await api.get("/locations", {
        params: {
          lat: coords.lat,
          lng: coords.lng,
          radius_km: radius,
          type: type || undefined,
          dog_friendly: dogFriendlyOnly ? "accessible" : undefined,
          limit: 80,
        },
      });
      return r.data.items;
    },
  });

  const best = useQuery({
    queryKey: ["best-walk", coords, radius],
    enabled,
    // Cap retries so a transient upstream weather-API blip doesn't leave
    // the hero stuck on a spinner for ~60s while TanStack backs off.
    retry: 1,
    queryFn: async () => {
      try {
        const r = await api.get("/best-walk", { params: { lat: coords.lat, lng: coords.lng, radius_km: Math.max(radius, 50) } });
        return r.data;
      } catch { return null; }
    },
  });

  // Compute weather emojis per visible card via best-walk's tier? Skip per-card weather fetch (expensive).
  const filtered = useMemo(() => {
    let items = list.data || [];
    // Client-side amenity filter — matches against the trail's own
    // `dog_amenities` tag array using the shared alias mapping. AND
    // semantics across chips (a trail must satisfy every active chip).
    if (amenityFilters.length > 0) {
      items = items.filter((l) => trailMatchesAllAmenities(l, amenityFilters));
    }
    if (goodWeatherOnly && best.data && best.data.walk_score?.tier === "avoid") {
      // simple heuristic — if today's overall conditions are 'avoid', surface a notice; we don't have per-loc weather here
      // We keep all items but the BestWalkCard's tier informs the user.
    }
    return items;
  }, [list.data, amenityFilters, goodWeatherOnly, best.data]);

  // Cache the top nearby result's region so subsequent bare telemetry events
  // (discover_view, map_view) can land on the admin heatmap instead of the
  // "untagged" bucket. Top result is the closest one — best signal we have.
  useEffect(() => {
    const topRegion = list.data?.[0]?.region;
    if (topRegion) rememberRegion(topRegion);
  }, [list.data]);

  return (
    <div className="max-w-2xl mx-auto px-4 sm:px-6 pt-6 pb-6">
      <header className="mb-5 flex items-center gap-3">
        <img
          src={LOGO_URL}
          alt="Ozzy"
          className="w-14 h-14 sm:w-16 sm:h-16 rounded-full border border-brand-border shadow-sm object-cover shrink-0"
        />
        <div className="min-w-0 flex-1">
          <div className="td-section-label">Basecamp</div>
          <h1 className="font-heading text-3xl sm:text-4xl font-semibold text-brand-text leading-tight mt-1">
            Discover walks
          </h1>
          <p className="text-brand-subtle mt-1 text-sm flex items-center gap-1.5">
            <MapPin size={14} />
            {geoState === "place"
              ? `Showing walks near ${coordsLabel}`
              : geoState === "granted"
                ? "Sorted by distance from your location"
                : `Showing walks near ${FALLBACK_COORDS.label} — enable location for nearby finds`}
          </p>
        </div>
        <button
          data-testid="open-search-btn"
          onClick={() => setSearchOpen(true)}
          className="shrink-0 w-11 h-11 rounded-full bg-brand-surface border border-brand-border flex items-center justify-center hover:bg-brand-sand transition-colors"
          aria-label="Search"
        >
          <Search size={18} className="text-brand-primary" />
        </button>
      </header>

      {/* Hero best-walk */}
      <div className="mb-6">
        {best.isLoading ? (
          <div className="td-card p-6 flex items-center justify-center text-brand-subtle gap-2">
            <Loader2 className="animate-spin" size={18} /> Finding today's best walk…
          </div>
        ) : best.data ? (
          <BestWalkCard data={best.data} />
        ) : null}
      </div>

      {/* Basecamps quick-browse — sits above the main trail feed so it's the
          first lateral recommendation set the user sees. Sorting + swipe
          behaviour handled by the carousel component. */}
      <BasecampsCarousel coords={coords} />

      {/* Filter chips — compact row that always fits on a 360px screen */}
      <div className="flex gap-2 items-center" data-testid="type-chips">
        {TYPES.map(({ key, label, Icon }) => {
          const active = type === key;
          return (
            <button
              key={key || "all"}
              data-testid={`chip-type-${key || "all"}`}
              onClick={() => setType(key)}
              className={`td-chip whitespace-nowrap ${active ? "td-chip-active" : ""}`}
            >
              <Icon size={13} /> {label}
            </button>
          );
        })}
        <button
          data-testid="chip-filters"
          onClick={() => setFiltersOpen(true)}
          className={`td-chip whitespace-nowrap ml-auto ${activeFilterCount > 0 ? "td-chip-active" : ""}`}
        >
          <SlidersHorizontal size={13} />
          Filters
          {amenityBadgeCount > 0 && (
            <span
              data-testid="filters-amenity-badge"
              aria-label={`${amenityBadgeCount} amenity ${amenityBadgeCount === 1 ? "filter" : "filters"} active`}
              className="ml-1 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-brand-accent text-white text-[10px] font-bold"
            >
              {amenityBadgeCount}
            </span>
          )}
        </button>
      </div>

      {/* Active filter summary line */}
      {activeFilterCount > 0 && (
        <div className="mt-2 text-xs text-brand-subtle flex flex-wrap items-center gap-x-3 gap-y-1">
          <span>Within <span className="text-brand-text font-medium">{formatRadius(radius, units)}</span></span>
          {goodWeatherOnly && best.data && (
            <span>· Good weather <span className="capitalize text-brand-text font-medium">{best.data.walk_score?.tier}</span> {best.data.walk_score?.emoji}</span>
          )}
          {dogFriendlyOnly && (
            <span>· Dog-friendly only</span>
          )}
        </div>
      )}

      {/* List */}
      <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-4" data-testid="discover-list">
        {list.isLoading && (
          <div className="col-span-full flex items-center justify-center py-12 text-brand-subtle">
            <Loader2 className="animate-spin mr-2" size={18} /> Loading walks…
          </div>
        )}
        {!list.isLoading && filtered.length === 0 && (
          <div className="col-span-full td-card p-6 text-center text-brand-subtle">
            No walks within {formatRadius(radius, units)}. Try a wider radius?
          </div>
        )}
        {filtered.map((loc) => (
          <TrailCard
            key={loc.location_id}
            loc={loc}
            weatherEmoji={null}
            isCachedOffline={offlineIds.has(loc.location_id)}
          />
        ))}
      </div>

      {/* Filters bottom sheet */}
      {filtersOpen && (
        <div
          className="fixed inset-0 z-[1100] bg-brand-text/60 flex items-end sm:items-center justify-center"
          onClick={() => setFiltersOpen(false)}
          data-testid="filters-sheet"
        >
          <div
            className="bg-brand-surface w-full sm:max-w-md sm:rounded-3xl rounded-t-3xl p-6 animate-fade-up"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between">
              <h3 className="font-heading text-2xl">Filters</h3>
              <button
                onClick={() => setFiltersOpen(false)}
                className="text-brand-subtle p-1"
                aria-label="Close filters"
                data-testid="filters-close"
              >
                <X size={20} />
              </button>
            </div>

            <div className="mt-5">
              <div className="td-section-label">Search radius</div>
              <div className="flex gap-2 mt-2">
                {RADII.map((r) => (
                  <button
                    key={r}
                    data-testid={`chip-radius-${r}`}
                    onClick={() => setRadius(r)}
                    className={`td-chip flex-1 justify-center !py-2 ${radius === r ? "td-chip-active" : ""}`}
                  >
                    {formatRadius(r, units)}
                  </button>
                ))}
              </div>
              <p className="text-xs text-brand-subtle mt-2">
                We'll only show walks within {formatRadius(radius, units)} of you.
              </p>
            </div>

            <div className="mt-6">
              <div className="td-section-label">Conditions</div>
              <div className="space-y-2 mt-2">
                <button
                  data-testid="chip-good-weather"
                  onClick={() => setGoodWeatherOnly((x) => !x)}
                  className={`w-full flex items-center gap-3 p-4 rounded-2xl border transition-colors ${
                    goodWeatherOnly ? "bg-brand-primary/10 border-brand-primary text-brand-text" : "bg-brand-sand border-brand-border text-brand-text"
                  }`}
                >
                  <Cloud size={18} className={goodWeatherOnly ? "text-brand-primary" : "text-brand-subtle"} />
                  <span className="text-left flex-1">
                    <span className="block font-medium">Good walk weather today</span>
                    <span className="block text-xs text-brand-subtle mt-0.5">Only walks where conditions are favourable.</span>
                  </span>
                  <span
                    className={`w-10 h-6 rounded-full transition-colors flex items-center px-0.5 ${
                      goodWeatherOnly ? "bg-brand-primary" : "bg-brand-border"
                    }`}
                  >
                    <span className={`w-5 h-5 rounded-full bg-brand-surface shadow transform transition-transform ${goodWeatherOnly ? "translate-x-4" : ""}`} />
                  </span>
                </button>

                <button
                  data-testid="chip-dog-friendly"
                  onClick={() => setDogFriendlyOnly((x) => !x)}
                  className={`w-full flex items-center gap-3 p-4 rounded-2xl border transition-colors ${
                    dogFriendlyOnly ? "bg-brand-primary/10 border-brand-primary text-brand-text" : "bg-brand-sand border-brand-border text-brand-text"
                  }`}
                >
                  <PawPrint size={18} className={dogFriendlyOnly ? "text-brand-primary" : "text-brand-subtle"} />
                  <span className="text-left flex-1">
                    <span className="block font-medium">Top dog-friendly</span>
                    <span className="block text-xs text-brand-subtle mt-0.5">Gates & kissing-gates only — no stiles or dog=no.</span>
                  </span>
                  <span
                    className={`w-10 h-6 rounded-full transition-colors flex items-center px-0.5 ${
                      dogFriendlyOnly ? "bg-brand-primary" : "bg-brand-border"
                    }`}
                  >
                    <span className={`w-5 h-5 rounded-full bg-brand-surface shadow transform transition-transform ${dogFriendlyOnly ? "translate-x-4" : ""}`} />
                  </span>
                </button>
              </div>
            </div>

            <div className="mt-6">
              <div className="td-section-label">Amenities</div>
              <div className="space-y-2 mt-2">
                {AMENITIES.map(({ key, label, desc, Icon }) => {
                  const on = amenityFilters.includes(key);
                  return (
                    <button
                      key={key}
                      data-testid={`chip-amenity-${key}`}
                      onClick={() => toggleAmenity(key)}
                      className={`w-full flex items-center gap-3 p-4 rounded-2xl border transition-colors ${
                        on ? "bg-brand-primary/10 border-brand-primary text-brand-text" : "bg-brand-sand border-brand-border text-brand-text"
                      }`}
                    >
                      <Icon size={18} className={on ? "text-brand-primary" : "text-brand-subtle"} />
                      <span className="text-left flex-1">
                        <span className="block font-medium">{label}</span>
                        <span className="block text-xs text-brand-subtle mt-0.5">{desc}</span>
                      </span>
                      <span
                        className={`w-10 h-6 rounded-full transition-colors flex items-center px-0.5 ${
                          on ? "bg-brand-primary" : "bg-brand-border"
                        }`}
                      >
                        <span className={`w-5 h-5 rounded-full bg-brand-surface shadow transform transition-transform ${on ? "translate-x-4" : ""}`} />
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="flex gap-2 mt-6">
              <button
                data-testid="filters-reset"
                onClick={() => { setRadius(20); setGoodWeatherOnly(false); setDogFriendlyOnly(false); setAmenityFilters([]); }}
                className="td-btn-secondary flex-1 !py-2.5"
              >
                Reset
              </button>
              <button
                data-testid="filters-apply"
                onClick={() => setFiltersOpen(false)}
                className="td-btn-primary flex-1 !py-2.5"
              >
                Show walks
              </button>
            </div>
          </div>
        </div>
      )}

      <SearchSheet
        open={searchOpen}
        onClose={() => setSearchOpen(false)}
        onPickLocation={(loc) => { track("map_search", { kind: "location", id: loc.location_id }, loc.region); navigate(`/location/${loc.location_id}`); }}
        onPickBasecamp={(b) => { track("map_search", { kind: "basecamp", id: b.basecamp_id }); navigate(`/basecamp/${b.basecamp_id}`); }}
        onPickPlace={(place) => {
          setCoords({ lat: place.lat, lng: place.lng });
          setCoordsLabel(place.name);
          setGeoState("place");
          track("map_search", { kind: "place", name: place.name, type: place.type }, place.county || place.name);
        }}
        placeholder="Search walks, towns, hills or postcodes…"
      />
    </div>
  );
}
